import React from 'react';
import View from "react-native-web/dist/exports/View";
import Platform from "react-native-web/dist/exports/Platform";
import StyleSheet from "react-native-web/dist/exports/StyleSheet";
import PropTypes from 'prop-types';
import { viewports } from '@telus-uds/system-constants';
import { applyTextStyles, useThemeTokens, applyOuterBorder, useTheme } from '../ThemeProvider';
import { a11yProps, componentPropType, getTokensPropType, inputSupportsProps, selectSystemProps, useInputValue, variantProp, viewProps, htmlAttrs } from '../utils';
import Picker from './Picker';
import InputSupports from '../InputSupports';
import { ANDROID_VALIDATION_ICON_CONTAINER_OFFSET } from './constants';
import { jsx as _jsx, jsxs as _jsxs } from "react/jsx-runtime";
const [selectProps, selectedSystemPropTypes] = selectSystemProps([a11yProps, inputSupportsProps, viewProps, htmlAttrs]);
const selectInputStyles = (_ref, themeOptions, inactive) => {
  let {
    backgroundColor,
    color,
    borderWidth = 0,
    borderColor,
    borderRadius,
    paddingTop,
    paddingBottom,
    paddingLeft = 0,
    paddingRight,
    fontName,
    fontSize,
    fontWeight,
    lineHeight,
    icon,
    iconSize = 0,
    validationIcon,
    validationIconSize = 0,
    height
  } = _ref;
  // Subtract border width from padding so overall input width/height doesn't
  // jump around if the border width changes (avoiding NaN and negative padding)
  const offsetBorder = value => typeof value === 'number' ? Math.max(0, value - borderWidth) : value;
  const textStyles = applyTextStyles({
    fontName,
    fontSize,
    lineHeight,
    fontWeight,
    themeOptions
  });
  const webStyles = Platform.select({
    web: {
      appearance: 'none',
      WebkitAppearance: 'none',
      // since iOS Safari needs a prefix
      outline: 'none',
      cursor: inactive ? 'not-allowed' : undefined,
      opacity: inactive ? 1 : undefined,
      // override Chrome's default fadeout of a disabled select
      // Enhanced fix for mobile dropdown positioning: restore native appearance on touch devices
      // Using multiple media queries and higher specificity to ensure it works in all contexts
      [`@media (pointer: coarse), (maxWidth: ${viewports.map.get(viewports.md)}px) and (hover: none)`]: {
        appearance: 'auto !important',
        WebkitAppearance: 'auto !important',
        backgroundImage: 'none !important'
      }
    }
  });
  let paddingWithIcons = paddingRight;
  if (Platform.OS !== 'android') {
    if (icon) {
      paddingWithIcons += iconSize;
    }
    if (validationIcon) {
      paddingWithIcons += validationIconSize;
    }
  }
  return {
    backgroundColor,
    color,
    borderWidth,
    borderColor,
    borderRadius,
    paddingLeft: offsetBorder(paddingLeft),
    paddingRight: offsetBorder(paddingWithIcons),
    paddingTop: offsetBorder(paddingTop),
    paddingBottom: offsetBorder(paddingBottom),
    height,
    ...textStyles,
    ...webStyles
  };
};
const selectOuterBorderStyles = _ref2 => {
  let {
    outerBackgroundColor,
    outerBorderWidth,
    outerBorderColor,
    borderRadius
  } = _ref2;
  return {
    background: outerBackgroundColor,
    ...applyOuterBorder({
      outerBorderColor,
      outerBorderWidth,
      borderRadius
    })
  };
};
const selectIconTokens = _ref3 => {
  let {
    iconSize,
    iconColor
  } = _ref3;
  return {
    size: iconSize,
    color: iconColor
  };
};
const selectIconContainerStyles = _ref4 => {
  let {
    paddingRight,
    paddingBottom
  } = _ref4;
  return {
    paddingRight: paddingRight + 4,
    marginRight: -4,
    paddingBottom
  };
};
const selectValidationIconTokens = _ref5 => {
  let {
    validationIconSize,
    validationIconColor
  } = _ref5;
  return {
    size: validationIconSize,
    color: validationIconColor
  };
};
const selectValidationIconContainerStyles = _ref6 => {
  let {
    icon,
    iconSize = 0,
    paddingRight = 0,
    paddingBottom
  } = _ref6;
  return {
    paddingRight: icon ? paddingRight + iconSize + 4 : paddingRight,
    ...(Platform.OS === 'android' ? {
      paddingBottom: paddingBottom + ANDROID_VALIDATION_ICON_CONTAINER_OFFSET
    } : {
      paddingBottom
    })
  };
};
const selectCustomFeedbackStyles = _ref7 => {
  let {
    feedbackBackgroundColor
  } = _ref7;
  return {
    backgroundColor: feedbackBackgroundColor
  };
};

/**
 * A basic form single-choice select component. Use in forms or individually to receive user's input.
 *
 * ## Usage criteria
 * - Include a `placeholder` to provide instructions such as "Please select..." as an unselectable option (recommended)
 * - Use when options are between 7-15 (recommended)
 * - For options are 6 and under, use `Radio` groups or `ButtonGroup`.
 * - For options 15 and above, look for alternatives (e.g grouping into categories, or input field with auto-complete etc.)
 *
 * ## Controlled component
 * If it is required that the state of the `Select` be controlled by the application or other external methods,
 * `value` and `onChange` props must be passed to the `Select`.
 * If the `Select` value should not be changed by user input, a `readOnly` prop must be provided.
 *
 * ## Uncontrolled component
 * If it is not necessary to control the `Select` state, you can create one without a value prop.
 * In this case its value can be accessed within the `onChange` callback. Use the `initialValue` prop to set the
 * default value for an uncontrolled `Select`.
 *
 * ## Getting feedback for entered values
 * Use the `feedback` prop to give the user feedback regarding their selection.
 * You can affirm that the user's selection was correct, or highlight errors that must be corrected, using the `validation` prop.
 * Keep feedback text as brief as possible, should be limited to text and links.
 *
 * ## Using a tooltip
 * When a more in-depth explanation is needed to fill out a `Select` properly, the tooltip prop may be used.
 *
 * ## Grouping options
 * `Select` supports option groups. Use a `Select.Group` component to render an `<optgroup>` element with its items.
 * NOTE: this does not work on native platforms - the grouped items will be shown at the same level as the non-grouped items.
 *
 */
const Select = /*#__PURE__*/React.forwardRef((_ref8, ref) => {
  let {
    value,
    initialValue,
    onChange,
    children,
    inactive,
    readOnly,
    placeholder,
    validation,
    tokens,
    variant,
    testID,
    ...rest
  } = _ref8;
  const {
    currentValue,
    setValue
  } = useInputValue({
    value,
    initialValue,
    onChange,
    readOnly
  });
  const [isFocused, setIsFocused] = React.useState(false);
  const handleFocus = () => setIsFocused(true);
  const handleBlur = () => setIsFocused(false);
  const [isHovered, setIsHovered] = React.useState(false);
  const handleMouseOver = () => setIsHovered(true);
  const handleMouseOut = () => setIsHovered(false);
  const [isOpen, setIsOpen] = React.useState(false);
  const {
    supportsProps,
    ...selectedProps
  } = selectProps(rest);
  const themeTokens = useThemeTokens('Select', tokens, variant, {
    focus: isFocused,
    hover: isHovered,
    inactive,
    active: isOpen,
    validation
  });
  const {
    icon: IconComponent,
    validationIcon: ValidationIconComponent
  } = themeTokens;
  const {
    themeOptions
  } = useTheme();
  return /*#__PURE__*/_jsx(InputSupports, {
    ...supportsProps,
    ...selectedProps,
    validation: validation,
    feedbackTokens: selectCustomFeedbackStyles(themeTokens),
    children: _ref9 => {
      let {
        inputId,
        ...props
      } = _ref9;
      return /*#__PURE__*/_jsxs(View, {
        style: selectOuterBorderStyles(themeTokens),
        children: [/*#__PURE__*/_jsx(Picker, {
          ref: ref,
          style: selectInputStyles(themeTokens, themeOptions, inactive),
          onFocus: handleFocus,
          onBlur: handleBlur,
          onMouseOver: handleMouseOver,
          onMouseOut: handleMouseOut,
          onOpen: () => setIsOpen(true),
          onClose: () => setIsOpen(false),
          onChange: e => setValue(e, null),
          value: currentValue,
          inactive: inactive,
          placeholder: placeholder !== undefined ? {
            label: placeholder,
            value: ''
          } : undefined,
          ...props,
          nativeID: inputId,
          testID: testID,
          children: children
        }), ValidationIconComponent && /*#__PURE__*/_jsx(View, {
          pointerEvents: "none",
          style: [staticStyles.iconContainer, selectValidationIconContainerStyles(themeTokens)],
          children: /*#__PURE__*/_jsx(ValidationIconComponent, {
            ...selectValidationIconTokens(themeTokens)
          })
        }), IconComponent && /*#__PURE__*/_jsx(View, {
          pointerEvents: "none",
          style: [staticStyles.iconContainer, selectIconContainerStyles(themeTokens)],
          children: /*#__PURE__*/_jsx(IconComponent, {
            ...selectIconTokens(themeTokens)
          })
        })]
      });
    }
  });
});
Select.displayName = 'Select';
Select.propTypes = {
  ...selectedSystemPropTypes,
  /**
   * Initial value for the uncontrolled version.
   */
  initialValue: PropTypes.string,
  /**
   * Current value to be set as selected.
   */
  value: PropTypes.string,
  /**
   * Callback to be called when the value changes.
   */
  onChange: PropTypes.func,
  /**
   * An array of items or item groups to be used as options.
   */
  children: componentPropType(['Item', 'Group']),
  /**
   * Whether the select is disabled.
   */
  inactive: PropTypes.bool,
  /**
   * Whether the select is read-only.
   */
  readOnly: PropTypes.bool,
  /**
   * A placeholder to provide instructions (such as "Please select...")
   * as an unselectable option
   */
  placeholder: PropTypes.string,
  /**
   * Select tokens.
   */
  tokens: getTokensPropType('Select'),
  /**
   * Select variant.
   */
  variant: variantProp.propType,
  /**
   * Dedicated ID for testing.
   */
  testID: PropTypes.string
};
export default Select;
const staticStyles = StyleSheet.create({
  iconContainer: {
    position: 'absolute',
    right: 0,
    bottom: 0
  }
});